// Topbar.js
import React, { useContext, useState, useRef, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { AuthContext } from "../AuthContext/AuthContext";
import HmLogo from "../Media/logo-icon.png";
import "./Topbar.css";

/* ════════════════════════════════════════════════════════════
   HELPERS DE FECHA
   ════════════════════════════════════════════════════════════ */
const MONTH_NAMES = [
  "Enero",
  "Febrero",
  "Marzo",
  "Abril",
  "Mayo",
  "Junio",
  "Julio",
  "Agosto",
  "Septiembre",
  "Octubre",
  "Noviembre",
  "Diciembre",
];
const DAY_NAMES = ["Do", "Lu", "Ma", "Mi", "Ju", "Vi", "Sá"];

const toYMD = (d) => {
  const p = (n) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}`;
};
const todayS = () => toYMD(new Date());
const fromYMD = (s) => (s ? new Date(s + "T12:00:00") : null);
const fmtShort = (ymd) => {
  if (!ymd) return "—";
  const d = fromYMD(ymd);
  return `${d.getDate()} ${MONTH_NAMES[d.getMonth()].slice(0, 3)} ${d.getFullYear()}`;
};

/* ════════════════════════════════════════════════════════════
   DATE RANGE PICKER
   ════════════════════════════════════════════════════════════ */
const DateRangePicker = ({ dateRange, onApply, onClose, style }) => {
  const today = todayS();
  const init = fromYMD(dateRange?.start) || new Date();

  const [viewY, setViewY] = useState(init.getFullYear());
  const [viewM, setViewM] = useState(init.getMonth());
  const [picking, setPicking] = useState("start");
  const [tmpS, setTmpS] = useState(dateRange?.start || "");
  const [hover, setHover] = useState("");
  const selfRef = useRef(null);

  useEffect(() => {
    const fn = (e) => {
      if (selfRef.current && !selfRef.current.contains(e.target)) onClose();
    };
    document.addEventListener("mousedown", fn);
    return () => document.removeEventListener("mousedown", fn);
  }, [onClose]);

  const prevM = () =>
    viewM === 0
      ? (setViewY((y) => y - 1), setViewM(11))
      : setViewM((m) => m - 1);
  const nextM = () =>
    viewM === 11
      ? (setViewY((y) => y + 1), setViewM(0))
      : setViewM((m) => m + 1);

  const daysInM = new Date(viewY, viewM + 1, 0).getDate();
  const firstIdx = new Date(viewY, viewM, 1).getDay();
  const cells = [
    ...Array(firstIdx).fill(null),
    ...Array.from({ length: daysInM }, (_, i) => i + 1),
  ];

  const handleDay = (day) => {
    const ymd = toYMD(new Date(viewY, viewM, day));
    if (ymd > today) return;
    if (picking === "start") {
      setTmpS(ymd);
      setPicking("end");
    } else {
      if (ymd === tmpS) {
        onApply({ start: ymd, end: ymd });
        onClose();
      } else if (ymd > tmpS) {
        onApply({ start: tmpS, end: ymd });
        onClose();
      } else {
        setTmpS(ymd);
      }
    }
  };

  let lo, hi;
  if (picking === "end") {
    lo = tmpS;
    hi = hover && hover > tmpS ? hover : "";
  } else {
    lo = dateRange?.start || "";
    hi =
      dateRange?.end && dateRange.end !== dateRange.start ? dateRange.end : "";
  }

  return (
    <div
      className="drp-popup"
      style={style}
      ref={selfRef}
      onMouseLeave={() => setHover("")}
    >
      <div className="drp-status-bar">
        <span className="drp-pulse-dot" />
        {picking === "start" ? (
          "Selecciona la fecha inicial"
        ) : (
          <span>
            Inicio: <strong>{fmtShort(tmpS)}</strong> — selecciona el fin (o
            clic mismo día)
          </span>
        )}
      </div>

      <div className="drp-cal-header">
        <button className="drp-nav-btn" onClick={prevM} title="Mes anterior">
          ‹
        </button>
        <span className="drp-month-label">
          {MONTH_NAMES[viewM]} {viewY}
        </span>
        <button className="drp-nav-btn" onClick={nextM} title="Mes siguiente">
          ›
        </button>
      </div>

      <div className="drp-weekdays">
        {DAY_NAMES.map((d) => (
          <span key={d}>{d}</span>
        ))}
      </div>

      <div className="drp-grid">
        {cells.map((day, idx) => {
          if (!day)
            return (
              <span key={`e${idx}`} className="drp-cell drp-cell--empty" />
            );
          const ymd = toYMD(new Date(viewY, viewM, day));
          const future = ymd > today;
          const isLo = ymd === lo;
          const isHi = !!(hi && ymd === hi);
          const inRng = !!(lo && hi && lo !== hi && ymd > lo && ymd < hi);
          const isTdy = ymd === today && !isLo && !isHi;
          const cls = [
            "drp-cell",
            future && "drp-cell--off",
            isLo && isHi && "drp-cell--single",
            isLo && !isHi && "drp-cell--lo",
            isHi && !isLo && "drp-cell--hi",
            inRng && "drp-cell--range",
            isTdy && "drp-cell--today",
          ]
            .filter(Boolean)
            .join(" ");
          return (
            <button
              key={day}
              className={cls}
              disabled={future}
              onClick={() => handleDay(day)}
              onMouseEnter={() => picking === "end" && !future && setHover(ymd)}
            >
              <span className="drp-day-inner">{day}</span>
            </button>
          );
        })}
      </div>

      <div className="drp-footer">
        <button
          className="drp-foot-btn"
          onClick={() => {
            const t = todayS();
            onApply({ start: t, end: t });
            onClose();
          }}
        >
          Hoy
        </button>
        <span className="drp-foot-range">
          {lo ? fmtShort(lo) : "Sin selección"}
          {hi && hi !== lo && (
            <>
              {" "}
              <span className="drp-arrow">→</span> {fmtShort(hi)}
            </>
          )}
        </span>
        <button
          className="drp-foot-btn drp-foot-btn--ghost"
          onClick={() => {
            setTmpS("");
            setPicking("start");
            setHover("");
          }}
        >
          Limpiar
        </button>
      </div>
    </div>
  );
};

/* ════════════════════════════════════════════════════════════
   HELPERS CONEXIÓN Y NAV
   ════════════════════════════════════════════════════════════ */
/* La conexión se basa en el ÚLTIMO LATIDO (last_seen / keepalive), provisto por
   el backend en deviceStatus.status, NO en la última lectura de sensor ni en el
   rango de fechas. Un chip puede estar vivo (keepalive cada ~60 s) sin emitir
   eventos de sensor. Mapea el status del backend a la config visual.            */
const STATUS_MAP = {
  online: "connected",
  unstable: "unstable",
  offline: "disconnected",
};
const connKeyFromStatus = (deviceStatus) => {
  if (!deviceStatus?.status) return null; // aún sin respuesta → no se pinta la píldora
  return STATUS_MAP[deviceStatus.status] || "disconnected";
};
const CONN_CFG = {
  connected: {
    label: "Conectado",
    color: "#4cde8a",
    border: "rgba(76,222,138,0.45)",
  },
  unstable: {
    label: "Inestable",
    color: "#ffa53e",
    border: "rgba(255,165,62,0.45)",
  },
  disconnected: {
    label: "Sin conexión",
    color: "#ff5c5c",
    border: "rgba(255,92,92,0.45)",
  },
};
const CONN_DOT = { connected: "●", unstable: "◐", disconnected: "○" };

/* Formatea el tiempo desde la última conexión para mostrar en el pill */
const formatLastSeen = (deviceStatus) => {
  if (deviceStatus?.seconds_ago == null) return null;
  const sa = deviceStatus.seconds_ago;
  if (sa < 60) return `hace ${sa}s`;
  if (sa < 3600) return `hace ${Math.floor(sa / 60)} min`;
  if (sa < 86400) return `hace ${Math.floor(sa / 3600)}h`;
  return deviceStatus.last_seen?.substring(5, 16) || null; // "MM-DD HH:MM"
};

const NAV_MODULES = [
  { key: "dashboard", label: "Dashboard", icon: "📊", privKey: "dashboard" },
  { key: "users", label: "Usuarios", icon: "👥", privKey: "users" },
  { key: "devices", label: "Dispositivos", icon: "🖥", privKey: "devices" },
];

/* Mapeo de clave de módulo → ruta de React Router */
const MODULE_ROUTES = {
  dashboard: "/home",
  users: "/users",
  devices: "/devices",
};

/* ════════════════════════════════════════════════════════════
   TOPBAR
   ════════════════════════════════════════════════════════════ */
const Topbar = ({
  devices = null,
  selectedDevice = null,
  onDeviceChange = null,
  dateRange = null,
  onDateRangeChange = null,
  reading = null,
  deviceStatus = null,
  userType = 0,
  loadingDevices = false,
  activeModule = "dashboard",
  onNavigate = null,
  alerts = [],
  onDeleteAlert = null,
  // v1.1 — permite que otros dashboards (ej. DashboardB, portafolio
  // institucional) inyecten su PROPIO set de grupos de contexto en la
  // misma barra azul, sin necesitar el concepto de "dispositivo
  // seleccionado". Si se pasa, tiene prioridad total sobre la lógica de
  // contexto basada en dispositivo (que sigue intacta para Dashboard A).
  customCtxGroups = null,
}) => {
  const { user, logout } = useContext(AuthContext);
  const navigate = useNavigate();

  const [userMenu, setUserMenu] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [mapOpen, setMapOpen] = useState(false);
  const [calOpen, setCalOpen] = useState(false);
  const [calStyle, setCalStyle] = useState({});
  const [alertsOpen, setAlertsOpen] = useState(false);
  const dateGroupRef = useRef(null);

  /* Navegar — usa callback del padre si existe, si no navega internamente */
  const handleNavigate = (key) => {
    if (onNavigate) {
      onNavigate(key);
    } else {
      navigate(MODULE_ROUTES[key] || "/home");
    }
    setUserMenu(false);
  };

  const openCal = () => {
    if (dateGroupRef.current) {
      const r = dateGroupRef.current.getBoundingClientRect();
      setCalStyle({
        top: r.bottom + 6,
        left: Math.max(8, Math.min(r.left, window.innerWidth - 320)),
      });
    }
    setCalOpen(true);
  };

  // v1.1 — hasDashboard ahora también es true si viene customCtxGroups
  // (portafolio), no solo cuando hay un array de "devices" (device único).
  const hasDashboard = !!devices || !!customCtxGroups;
  const hasCoords = selectedDevice?.latitude && selectedDevice?.longitude;
  const connKey = hasDashboard ? connKeyFromStatus(deviceStatus) : null;
  const conn = connKey ? CONN_CFG[connKey] : null;
  const privs = user?.privileges || {};
  const activeAlertCount = alerts.filter((a) => a.status === "active").length;

  const effRange = dateRange || { start: todayS(), end: todayS() };
  const dateLabel =
    effRange.start === effRange.end
      ? fmtShort(effRange.start)
      : `${fmtShort(effRange.start)} — ${fmtShort(effRange.end)}`;

  /* ── Grupos de contexto (solo en dashboard) ────────────── */
  // v1.1 — customCtxGroups (si viene) manda por completo; la lógica de
  // abajo (basada en dispositivo) sigue exactamente igual para Dashboard A.
  const ctxGroups = customCtxGroups
    ? customCtxGroups
    : !hasDashboard
      ? []
      : [
        {
          id: "device",
          label: "Dispositivo",
          content: loadingDevices ? (
            <span className="tb-ctx-empty">Cargando…</span>
          ) : !devices.length ? (
            <span className="tb-ctx-empty">Sin dispositivos</span>
          ) : (
            <select
              className="tb-ctx-select"
              value={selectedDevice?.id_device || ""}
              onChange={(e) => {
                const d = devices.find(
                  (x) => x.id_device === Number(e.target.value),
                );
                if (d && onDeviceChange) onDeviceChange(d);
              }}
            >
              {devices.map((d) => (
                <option key={d.id_device} value={d.id_device}>
                  {d.keycode}
                  {userType === 0 ? ` — ${d.user_name || ""}` : ""}
                </option>
              ))}
            </select>
          ),
        },
        {
          id: "date",
          label: "Fecha",
          content: (
            <button
              ref={dateGroupRef}
              className={`tb-date-trigger${calOpen ? " tb-date-trigger--open" : ""}`}
              onClick={calOpen ? () => setCalOpen(false) : openCal}
              title="Seleccionar rango de fechas"
            >
              <span className="tb-date-text">{dateLabel}</span>
              <span className="tb-date-icon">📅</span>
            </button>
          ),
        },
        selectedDevice && {
          id: "system",
          label: "Sistema",
          content: <span className="tb-keycode">{selectedDevice.keycode}</span>,
        },
        conn && {
          id: "conn",
          label: "Conexión",
          content: (() => {
            const lastSeenStr = formatLastSeen(deviceStatus);
            return (
              <span
                className="tb-conn-pill"
                style={{ "--c": conn.color, "--b": conn.border }}
              >
                <span className="tb-conn-dot">{CONN_DOT[connKey]}</span>
                <span className="tb-conn-text">
                  {conn.label}
                  {lastSeenStr && (
                    <span className="tb-conn-time"> · {lastSeenStr}</span>
                  )}
                </span>
              </span>
            );
          })(),
        },
        hasCoords && {
          id: "location",
          label: "Ubicación",
          content: (
            <span className="tb-coords-wrap">
              <span className="tb-coords">
                {selectedDevice.latitude}, {selectedDevice.longitude}
              </span>
              <button className="tb-map-btn" onClick={() => setMapOpen(true)}>
                📍 Mapa
              </button>
            </span>
          ),
        },
        selectedDevice && {
          id: "install",
          label: "Instalación",
          content: (
            <span className="tb-chips-wrap">
              {selectedDevice.user_name && (
                <span className="tb-chip">👤 {selectedDevice.user_name}</span>
              )}
              <span className="tb-chip">
                🏠{" "}
                {selectedDevice.roof_area_m2
                  ? `${selectedDevice.roof_area_m2} m²`
                  : "—"}
              </span>
              <span className="tb-chip">
                🛢{" "}
                {selectedDevice.tank_capacity_l
                  ? `${Number(selectedDevice.tank_capacity_l).toLocaleString()} L`
                  : "—"}
              </span>
              <span className="tb-chip">
                ⚡ {selectedDevice.hp ? `${selectedDevice.hp} HP` : "—"}
              </span>
            </span>
          ),
        },
      ].filter(Boolean);

  /* ── Render ──────────────────────────────────────────────── */
  return (
    <>
      <header className="topbar">
        {/* Brand */}
        <div className="tb-brand">
          <img src={HmLogo} alt="HidroMetrics" className="tb-logo-img" />
          <span className="tb-name">HidroMetrics</span>
        </div>

        {/* Separador + contexto (solo dashboard) */}
        {hasDashboard && <span className="tb-vsep" />}
        {hasDashboard && (
          <div className="tb-ctx-scroll">
            {ctxGroups.map((g, i) => (
              <React.Fragment key={g.id}>
                {i > 0 && <span className="tb-ctx-divider" />}
                <div className="tb-ctx-group">
                  <span className="tb-ctx-label">{g.label}</span>
                  <div className="tb-ctx-content">{g.content}</div>
                </div>
              </React.Fragment>
            ))}
          </div>
        )}

        {/* Sección derecha — siempre al extremo derecho por margin-left:auto en CSS */}
        <div className="tb-right">
          {/* Hamburguesa mobile (solo en dashboard) */}
          {hasDashboard && (
            <button
              className={`tb-hamburger${mobileOpen ? " tb-hamburger--open" : ""}`}
              onClick={() => setMobileOpen((v) => !v)}
              aria-label="Menú del dispositivo"
            >
              <span />
              <span />
              <span />
            </button>
          )}

          {/* Campana de alertas (solo en dashboard) */}
          {hasDashboard && (
            <div className="tb-bell-wrap">
              <button
                className={`tb-bell-btn${alertsOpen ? " tb-bell-btn--open" : ""}`}
                onClick={() => setAlertsOpen((v) => !v)}
                onBlur={() => setTimeout(() => setAlertsOpen(false), 200)}
                title="Alertas del sistema"
              >
                <span className="tb-bell-icon">🔔</span>
                {activeAlertCount > 0 && (
                  <span className="tb-bell-badge">
                    {activeAlertCount > 9 ? "9+" : activeAlertCount}
                  </span>
                )}
              </button>

              {alertsOpen && (
                <div
                  className="tb-alerts-panel"
                  onMouseDown={(e) => e.preventDefault()}
                >
                  <div className="tb-ap-header">
                    <span className="tb-ap-title">Alertas</span>
                    <span className="tb-ap-count">
                      {activeAlertCount} activa
                      {activeAlertCount !== 1 ? "s" : ""}
                    </span>
                  </div>

                  <div className="tb-ap-list">
                    {alerts.length === 0 ? (
                      <div className="tb-ap-empty">
                        Sin alertas para este período
                      </div>
                    ) : (
                      alerts.map((a, i) => (
                        <div
                          key={a.id_alert || i}
                          className={`tb-ap-item tb-ap-item--${a.severity || "operational"}`}
                        >
                          <div className="tb-ap-item-left">
                            <span className="tb-ap-sev">
                              {a.severity === "critical" ? "🔴" : "🟡"}
                            </span>
                            <div className="tb-ap-info">
                              <span className="tb-ap-type">{a.alert_type}</span>
                              <span className="tb-ap-time">
                                {a.triggered_at}
                              </span>
                            </div>
                          </div>
                          <div className="tb-ap-item-right">
                            <span
                              className={`tb-ap-status tb-ap-status--${a.status}`}
                            >
                              {a.status === "active" ? "Activa" : "Resuelta"}
                            </span>
                            {onDeleteAlert && (
                              <button
                                className="tb-ap-delete"
                                title="Eliminar alerta"
                                onMouseDown={() => onDeleteAlert(a.id_alert)}
                              >
                                🗑️
                              </button>
                            )}
                          </div>
                        </div>
                      ))
                    )}
                  </div>

                  <div className="tb-ap-footer">
                    <button
                      className="tb-ap-detail-btn"
                      onMouseDown={() => {
                        setAlertsOpen(false);
                        setTimeout(() => {
                          document
                            .getElementById("hm-alerts-section")
                            ?.scrollIntoView({
                              behavior: "smooth",
                              block: "start",
                            });
                        }, 120);
                      }}
                    >
                      Ver detalles ↓
                    </button>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Botón de usuario */}
          <div className="tb-user-wrap">
            <button
              className="tb-user-btn"
              onClick={() => setUserMenu((v) => !v)}
              onBlur={() => setTimeout(() => setUserMenu(false), 150)}
            >
              <span className="tb-avatar">
                {(user?.full_name || user?.username || "U")[0].toUpperCase()}
              </span>
              <span className="tb-user-name">
                {user?.full_name || user?.username}
              </span>
              <span
                className={`tb-chevron${userMenu ? " tb-chevron--up" : ""}`}
              >
                ▾
              </span>
            </button>

            {userMenu && (
              <div className="tb-dropdown">
                <div className="tb-dd-head">
                  <div className="tb-dd-name">{user?.full_name}</div>
                  <div className="tb-dd-role">
                    {userType === 0 ? "Administrador" : "Cliente"}
                  </div>
                </div>

                {userType === 0 && (
                  <>
                    <div className="tb-dd-section-label">Módulos</div>
                    {NAV_MODULES.map((m) => {
                      const ok =
                        m.privKey === "dashboard"
                          ? privs.dashboard >= 1
                          : privs[m.privKey] >= 1;
                      if (!ok) return null;
                      return (
                        <button
                          key={m.key}
                          className={`tb-dd-nav-btn${activeModule === m.key ? " tb-dd-nav-btn--active" : ""}`}
                          onMouseDown={() => handleNavigate(m.key)}
                        >
                          <span className="tb-dd-nav-icon">{m.icon}</span>
                          {m.label}
                        </button>
                      );
                    })}
                  </>
                )}

                <hr className="tb-dd-divider" />
                <button className="tb-dd-logout" onMouseDown={logout}>
                  ↩ Cerrar sesión
                </button>
              </div>
            )}
          </div>
        </div>
        {/* /tb-right */}
      </header>

      {/* DATE RANGE PICKER */}
      {calOpen && (
        <DateRangePicker
          dateRange={effRange}
          onApply={(range) => {
            onDateRangeChange && onDateRangeChange(range);
            setCalOpen(false);
          }}
          onClose={() => setCalOpen(false)}
          style={{ position: "fixed", zIndex: 300, ...calStyle }}
        />
      )}

      {/* PANEL MOBILE */}
      {mobileOpen && hasDashboard && (
        <div className="tb-mobile-panel">
          {ctxGroups.map((g) => (
            <div key={g.id} className="tb-mobile-row">
              <span className="tb-mobile-label">{g.label}</span>
              <div className="tb-mobile-content">{g.content}</div>
            </div>
          ))}
        </div>
      )}

      {/* MODAL MAPA */}
      {mapOpen && hasCoords && (
        <div className="hm-modal-overlay" onClick={() => setMapOpen(false)}>
          <div className="hm-map-modal" onClick={(e) => e.stopPropagation()}>
            <div className="hm-map-modal-header">
              <div>
                <div className="hm-map-modal-title">
                  {selectedDevice.keycode}
                </div>
                <div className="hm-map-modal-coords">
                  {selectedDevice.latitude}, {selectedDevice.longitude}
                </div>
              </div>
              <button
                className="hm-map-close"
                onClick={() => setMapOpen(false)}
              >
                ✕
              </button>
            </div>
            <div className="hm-map-frame">
              <iframe
                title="Ubicación"
                src={`https://maps.google.com/maps?q=${selectedDevice.latitude},${selectedDevice.longitude}&z=16&output=embed`}
                width="100%"
                height="100%"
                style={{ border: 0 }}
                allowFullScreen=""
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
              />
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default Topbar;